import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from '@/app/layout/AppLayout';
import { RequireAuth } from '@/app/router/RequireAuth';
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { ForbiddenPage } from '@/app/pages/ForbiddenPage';
import { PlaceholderPage } from '@/app/pages/PlaceholderPage';
import { ManageClassificationPage } from '@/features/reports/pages/ManageClassificationPage';
import { SchoolProfileDashboardPage } from '@/features/reports/pages/SchoolProfileDashboardPage';
import { RiskTriageReportPage } from '@/features/reports/pages/RiskTriageReportPage';
import { TeacherEquityReport } from '@/features/reports/pages/TeacherEquityReport';
import { RegionalComparisonsPage } from '@/features/reports/pages/RegionalComparisonsPage';
import { DashboardSettingsPage } from '@/features/settings/pages/DashboardSettingsPage';
import { CompliancesPage } from '@/features/reports/pages/CompliancesPage';
import { GroupComparisonsPage } from '@/features/reports/pages/GroupComparisonsPage';
import { TuitionFeeBenchmarkPage } from '@/features/reports/pages/TuitionFeeBenchmarkPage';
import { SchoolSizeCostAnalysisPage } from '@/features/reports/pages/SchoolSizeCostAnalysisPage';
import { HomeEntryPage } from '@/app/pages/HomeEntryPage';
import { RequireFullAccess } from '@/app/router/RequireFullAccess';

export const ROUTES = {
  main: '/',
  login: '/login',
  forbidden: '/forbidden',
  reportsManageClassification: '/reports/manage-classification',
  reportsSchoolProfile: '/reports/school-profile',
  reportsRiskTriage: '/reports/risk-triage',
  reportsRegionalComparisons: '/reports/regional-comparisons',
  reportsCompliances: '/reports/compliances',
  reportsGroupComparisons: '/reports/group-comparisons',
  reportsTuitionFeeBenchmark: '/reports/tuition-fee-benchmark',
  reportsSchoolSizeCost: '/reports/school-size-cost',
  settings: '/settings',
} as const;

/** Deep link to School Profile with school + reporting year pre-selected. */
export function schoolProfileHref(params: {
  schoolId: string;
  year: string | number;
  governorateId?: number;
}): string {
  const q = new URLSearchParams();
  q.set('schoolId', params.schoolId.trim());
  q.set('year', String(params.year).trim());
  if (params.governorateId != null && Number.isFinite(params.governorateId)) {
    q.set('governorateId', String(params.governorateId));
  }
  return `${ROUTES.reportsSchoolProfile}?${q.toString()}`;
}

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/forbidden',
    element: <ForbiddenPage />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <HomeEntryPage /> },
      { path: 'tuition', element: <Navigate to="/" replace /> },
      { path: 'tuition-analysis', element: <Navigate to="/" replace /> },
      { path: 'tuition-ranges', element: <Navigate to="/" replace /> },
      { path: 'tuition-statistics', element: <Navigate to="/" replace /> },
      {
        path: 'reports/manage-classification',
        element: (
          <RequireFullAccess>
            <ManageClassificationPage />
          </RequireFullAccess>
        ),
      },
      { path: 'reports/school-profile', element: <SchoolProfileDashboardPage /> },
      {
        path: 'reports/risk-triage',
        element: (
          <RequireFullAccess>
            <RiskTriageReportPage />
          </RequireFullAccess>
        ),
      },
      { path: 'reports/teacher-equity', element: <TeacherEquityReport /> },
      {
        path: 'reports/regional-comparisons',
        element: (
          <RequireFullAccess>
            <RegionalComparisonsPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'reports/compliances',
        element: (
          <RequireFullAccess>
            <CompliancesPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'reports/group-comparisons',
        element: (
          <RequireFullAccess>
            <GroupComparisonsPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'reports/tuition-fee-benchmark',
        element: (
          <RequireFullAccess>
            <TuitionFeeBenchmarkPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'reports/school-size-cost',
        element: (
          <RequireFullAccess>
            <SchoolSizeCostAnalysisPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'placeholder/:id',
        element: (
          <RequireFullAccess>
            <PlaceholderPage />
          </RequireFullAccess>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequireFullAccess>
            <DashboardSettingsPage />
          </RequireFullAccess>
        ),
      },
    ],
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);
